import React, { useState, useEffect } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Plus, X } from 'lucide-react';
import './LeadModal.css';

const LeadModal = ({ isOpen, onClose, leadToEdit }) => {
    const { addLead, updateLead, packages = [], branches = [], sources = [] } = useSchool();

    const [newLead, setNewLead] = useState({
        name: '',
        phone: '',
        email: '',
        interest: '',
        branch: '',
        source: '',
        notes: '',
        status: 'New'
    });

    // Pre-fill defaults or edit data
    useEffect(() => {
        if (isOpen) {
            if (leadToEdit) {
                setNewLead({
                    ...leadToEdit,
                    // Ensure defaults if null
                    interest: leadToEdit.interest || '',
                    branch: leadToEdit.branch || '',
                    source: leadToEdit.source || '',
                    status: leadToEdit.status || 'New',
                    notes: leadToEdit.notes || ''
                });
            } else {
                // Reset for new entry
                setNewLead({
                    name: '',
                    phone: '',
                    email: '',
                    interest: packages[0]?.id || '',
                    branch: (branches[0] && typeof branches[0] === 'object') ? (branches[0].branch_name || branches[0].name || '') : (branches[0] || ''),
                    source: '',
                    notes: '',
                    status: 'New'
                });
            }
        }
    }, [isOpen, leadToEdit, packages, branches]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewLead(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (leadToEdit) {
            updateLead(newLead);
        } else {
            addLead(newLead);
        }

        onClose();
        // Form reset handled by useEffect on next open
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
            <div className="modal-content lead-modal">
                <div className="modal-header">
                    <div className="flex items-center gap-3">
                        <div className="plus-icon-bg">
                            <Plus size={20} className="text-gold" />
                        </div>
                        <h3 className="modal-title">{leadToEdit ? 'Edit Lead' : 'Add New Lead'}</h3>
                    </div>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="premium-form modal-form">
                    <div className="form-group">
                        <label className="form-label">FULL NAME</label>
                        <input
                            type="text"
                            name="name"
                            className="form-input"
                            placeholder="e.g. John Doe"
                            required
                            value={newLead.name}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">PHONE NUMBER</label>
                        <input
                            type="tel"
                            name="phone"
                            className="form-input"
                            placeholder="e.g. +263 77..."
                            required
                            value={newLead.phone}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label className="form-label">INTEREST</label>
                            <select name="interest" className="form-input" value={newLead.interest} onChange={handleInputChange}>
                                <option value="">Select interest...</option>
                                {(packages || []).map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">BRANCH</label>
                            <select name="branch" className="form-input" value={newLead.branch} onChange={handleInputChange}>
                                {(branches || []).map((b, index) => {
                                    const branchValue = (typeof b === 'object') ? (b.branch_name || b.name || '') : b;
                                    const branchId = (typeof b === 'object') ? (b.id || index) : index;
                                    return (
                                        <option key={branchId} value={branchValue}>{branchValue}</option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">WHERE DID YOU HEAR ABOUT US?</label>
                        <select
                            name="source"
                            className="form-input"
                            required
                            value={newLead.source}
                            onChange={handleInputChange}
                        >
                            <option value="">Select source...</option>
                            {(sources || []).map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">NOTES</label>
                        <textarea
                            name="notes"
                            className="form-input"
                            placeholder="Additional details..."
                            rows="4"
                            value={newLead.notes}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-save">{leadToEdit ? 'Save Changes' : 'Save Lead'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LeadModal;
